'use client'

import type { DialogProps } from '@mui/material'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { type ReactNode, useCallback, useRef, useState } from 'react'

export type ConfirmOptions = {
  title: string
  body?: string
  /** Confirm button label. */
  confirmLabel: string
  /** Cancel button label. */
  cancelLabel: string
  /** Render the confirm action in the error color for destructive operations. Defaults to false. */
  destructive?: boolean
  /** Dialog max width. Defaults to 'xs'; use 'sm' when the button labels need a single line. */
  maxWidth?: DialogProps['maxWidth']
}

type PendingState = ConfirmOptions & { resolve: (confirmed: boolean) => void }

/**
 * Promise-based confirmation dialog, scoped to a single component (no global provider). Returns an imperative
 * `confirm(options)` that resolves to `true` when the user accepts and `false` otherwise, plus a `confirmationDialog`
 * node the caller renders once. Copy is passed in by the caller so it stays localized at the call site.
 *
 * @example
 *   const { confirm, confirmationDialog } = useConfirm()
 *   const onDelete = async () => {
 *     if (!(await confirm({ title: t('delete_title'), confirmLabel: t('action_remove'), cancelLabel: t('action_cancel'), destructive: true }))) return
 *     await remove()
 *   }
 *   return (<>{...}{confirmationDialog}</>)
 */
export function useConfirm() {
  const [pending, setPending] = useState<PendingState | null>(null)
  const pendingRef = useRef<PendingState | null>(null)

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      const state = { ...options, resolve }
      pendingRef.current = state
      setPending(state)
    })
  }, [])

  const settle = useCallback((confirmed: boolean) => {
    pendingRef.current?.resolve(confirmed)
    pendingRef.current = null
    setPending(null)
  }, [])

  const confirmationDialog: ReactNode = (
    <Dialog open={pending != null} onClose={() => settle(false)} maxWidth={pending?.maxWidth ?? 'xs'} fullWidth>
      {pending && (
        <>
          <DialogTitle>{pending.title}</DialogTitle>
          {pending.body && (
            <DialogContent>
              <DialogContentText>{pending.body}</DialogContentText>
            </DialogContent>
          )}
          <DialogActions>
            <Button onClick={() => settle(false)}>{pending.cancelLabel}</Button>
            <Button
              onClick={() => settle(true)}
              variant="contained"
              color={pending.destructive ? 'error' : 'primary'}
              autoFocus
            >
              {pending.confirmLabel}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )

  return { confirm, confirmationDialog }
}
